/* THE SKILLS RECOVERY CONSOLE
   Robust cart drawer controller
*/

(() => {
  if (window.__skillsCartDrawerControllerReady) return;
  window.__skillsCartDrawerControllerReady = true;

  class SkillsCartDrawer extends HTMLElement {
    constructor() {
      super();

      this.activeElement = null;

      this.handleKeyup = this.handleKeyup.bind(this);
      this.handleOverlayClick = this.handleOverlayClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('keyup', this.handleKeyup);
      this.bindOverlay();
      this.setHeaderCartIconAccessibility();
    }

    disconnectedCallback() {
      this.removeEventListener('keyup', this.handleKeyup);
    }

    handleKeyup(event) {
      if (event.code === 'Escape') {
        this.close();
      }
    }

    handleOverlayClick(event) {
      if (
        event.target.id === 'CartDrawer-Overlay' ||
        event.target.classList.contains('cart-drawer__overlay')
      ) {
        this.close();
      }
    }

    bindOverlay() {
      const overlay = this.querySelector('#CartDrawer-Overlay');

      if (!overlay) return;

      if (overlay.dataset.skillsBound === 'true') return;

      overlay.dataset.skillsBound = 'true';

      overlay.addEventListener('click', this.handleOverlayClick);
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');

      if (!cartLink) return;

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute(
        'aria-expanded',
        this.classList.contains('active')
      );
    }

    open(triggeredBy) {

      if (triggeredBy) {
        this.setActiveElement(triggeredBy);
      }

      this.bindOverlay();

      const cartDrawerNote =
        this.querySelector('[id^="Details-"] summary');

      if (
        cartDrawerNote &&
        !cartDrawerNote.hasAttribute('role')
      ) {
        this.setSummaryAccessibility(cartDrawerNote);
      }

      this.classList.add('animate');

      requestAnimationFrame(() => {

        requestAnimationFrame(() => {

          this.classList.add('active');

          const cartLink =
            document.querySelector('#cart-icon-bubble');

          if (cartLink) {
            cartLink.setAttribute(
              'aria-expanded',
              'true'
            );
          }

        });

      });

      document.body.classList.add(
        'overflow-hidden',
        'skills-console-open'
      );

      window.setTimeout(() => {

        const focusContainer =
          this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : this.querySelector('#CartDrawer');

        const focusElement =
          this.querySelector('.drawer__close') ||
          this.querySelector('.drawer__inner');

        if (
          focusContainer &&
          focusElement &&
          typeof trapFocus === 'function'
        ) {
          trapFocus(
            focusContainer,
            focusElement
          );
        }

      }, 550);

    }

    close() {

      this.classList.remove('active');

      const cartLink =
        document.querySelector('#cart-icon-bubble');

      if (cartLink) {
        cartLink.setAttribute(
          'aria-expanded',
          'false'
        );
      }

      if (
        typeof removeTrapFocus === 'function'
      ) {
        removeTrapFocus(this.activeElement);
      }

      document.body.classList.remove(
        'overflow-hidden',
        'skills-console-open'
      );

    }
    setSummaryAccessibility(cartDrawerNote) {

      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute(
        'aria-expanded',
        'false'
      );

      const content =
        cartDrawerNote.nextElementSibling;

      if (content && content.id) {
        cartDrawerNote.setAttribute(
          'aria-controls',
          content.id
        );
      }

      cartDrawerNote.addEventListener(
        'click',
        (event) => {

          event.currentTarget.setAttribute(
            'aria-expanded',
            !event.currentTarget
              .closest('details')
              .hasAttribute('open')
          );

        }
      );

      if (
        cartDrawerNote.parentElement &&
        typeof onKeyUpEscape === 'function'
      ) {
        cartDrawerNote.parentElement.addEventListener(
          'keyup',
          onKeyUpEscape
        );
      }

    }

    renderContents(parsedState) {

      this.classList.toggle(
        'is-empty',
        Number(parsedState.item_count || 0) === 0
      );

      this.productId = parsedState.id;

      this.getSectionsToRender().forEach(
        (section) => {

          const sectionElement =
            section.selector
              ? document.querySelector(
                  section.selector
                )
              : document.getElementById(
                  section.id
                );

          if (
            !sectionElement ||
            !parsedState.sections[section.id]
          ) {
            return;
          }

          sectionElement.innerHTML =
            this.getSectionInnerHTML(
              parsedState.sections[
                section.id
              ],
              section.selector
            );

        }
      );

      this.bindOverlay();
      this.setHeaderCartIconAccessibility();

      window.setTimeout(() => {
        this.open();
      }, 30);

    }

    getSectionInnerHTML(
      html,
      selector = '.shopify-section'
    ) {

      const parsed =
        new DOMParser().parseFromString(
          html,
          'text/html'
        );

      const element =
        parsed.querySelector(selector);

      return element
        ? element.innerHTML
        : '';

    }

    getSectionsToRender() {

      return [

        {
          id: 'cart-drawer',
          selector: '#CartDrawer',
        },

        {
          id: 'cart-icon-bubble',
        },

      ];

    }

    setActiveElement(element) {
      this.activeElement = element;
    }

  }

  if (
    !customElements.get(
      'cart-drawer'
    )
  ) {

    customElements.define(
      'cart-drawer',
      SkillsCartDrawer
    );

  }
  /*
    Delegated handling.

    Shopify replaces #cart-icon-bubble every time the cart updates,
    so attaching the click directly to the element isn't reliable.
  */

  document.addEventListener(
    'click',
    (event) => {

      const cartLink =
        event.target.closest('#cart-icon-bubble');

      if (!cartLink) return;

      const drawer =
        document.querySelector('cart-drawer');

      if (!drawer) return;

      event.preventDefault();
      event.stopPropagation();

      drawer.open(cartLink);

    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {

      const cartLink =
        event.target.closest?.('#cart-icon-bubble');

      if (!cartLink) return;

      if (
        event.code !== 'Space' &&
        event.code !== 'Enter' &&
        event.key !== ' '
      ) {
        return;
      }

      const drawer =
        document.querySelector('cart-drawer');

      if (!drawer) return;

      event.preventDefault();

      drawer.open(cartLink);

    },
    true
  );

  /*
    Wait until Shopify has loaded CartItems before
    registering cart-drawer-items.
  */

  function registerCartDrawerItems() {

    if (
      customElements.get('cart-drawer-items')
    ) {
      return true;
    }

    if (
      typeof window.CartItems === 'undefined'
    ) {
      return false;
    }

    class SkillsCartDrawerItems extends window.CartItems {

      getSectionsToRender() {

        return [

          {
            id: 'CartDrawer',
            section: 'cart-drawer',
            selector: '.drawer__inner',
          },

          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },

        ];

      }

    }

    customElements.define(
      'cart-drawer-items',
      SkillsCartDrawerItems
    );

    return true;

  }

  if (!registerCartDrawerItems()) {

    let attempts = 0;

    const timer = window.setInterval(() => {

      attempts++;

      if (
        registerCartDrawerItems() ||
        attempts >= 100
      ) {
        window.clearInterval(timer);
      }

    }, 50);

  }

})();